import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { websiteService } from '../services/websiteService';

export const websiteKeys = {
  all: ['knowledge-base', 'websites'] as const,
  list: (knowledgeBaseId: string) => ['knowledge-base', 'websites', knowledgeBaseId] as const,
  pages: (websiteId: string, lastCrawledAt: string | null) =>
    ['knowledge-base', 'websites', 'pages', websiteId, lastCrawledAt] as const,
  page: (pageId: string) => ['knowledge-base', 'websites', 'page', pageId] as const,
};

// Polls while an import or the indexing of pages runs, so the status updates
const POLL_INTERVAL_MS = 3000;

export const useWebsites = (
  knowledgeBaseId: string,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useQuery({
    queryKey: websiteKeys.list(knowledgeBaseId),
    queryFn: () => websiteService.list(knowledgeBaseId),
    enabled,
    refetchInterval: query =>
      query.state.data?.some(website => website.crawl_status === 'crawling')
        ? POLL_INTERVAL_MS
        : false,
  });

// The pages of a crawl are stored when it ends, after the last poll during the crawl;
// the new last_crawled_at in the key loads them
export const useWebsitePages = (
  websiteId: string | undefined,
  isCrawling: boolean,
  lastCrawledAt: string | null
) =>
  useQuery({
    queryKey: websiteKeys.pages(websiteId ?? '', lastCrawledAt),
    queryFn: () => websiteService.pages(websiteId as string),
    enabled: !!websiteId,
    placeholderData: keepPreviousData,
    refetchInterval: query =>
      isCrawling || query.state.data?.some(page => page.index_status === 'processing')
        ? POLL_INTERVAL_MS
        : false,
  });

export const useWebsitePage = (pageId: string | null) =>
  useQuery({
    queryKey: websiteKeys.page(pageId ?? ''),
    queryFn: () => websiteService.page(pageId as string),
    enabled: !!pageId,
  });

const useInvalidateWebsites = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: websiteKeys.all });
};

export const useCreateWebsite = (knowledgeBaseId: string) => {
  const invalidate = useInvalidateWebsites();
  return useMutation({
    mutationFn: (rootUrl: string) => websiteService.create(knowledgeBaseId, rootUrl),
    onSuccess: invalidate,
  });
};

export const useDeleteWebsite = () => {
  const invalidate = useInvalidateWebsites();
  return useMutation({
    mutationFn: (id: string) => websiteService.remove(id),
    onSuccess: invalidate,
  });
};

export const useCrawlWebsite = () => {
  const invalidate = useInvalidateWebsites();
  return useMutation({
    mutationFn: (id: string) => websiteService.crawl(id),
    onSettled: invalidate,
  });
};

export const useSelectWebsitePages = () => {
  const invalidate = useInvalidateWebsites();
  return useMutation({
    mutationFn: ({
      websiteId,
      included,
      excluded,
    }: {
      websiteId: string;
      included?: string[];
      excluded?: string[];
    }) => websiteService.select(websiteId, { included, excluded }),
    onSuccess: invalidate,
  });
};
